import { DataDai } from '../domain/dai/types';
import { Hall } from '../domain/schemas';

import { parseNumber } from './parseNumber';

type GetTransitionHallsFromRowParams = {
  rowHalls: string[] | undefined;
  fallbackHallCode: string | undefined;
};

/**
 * 台別データ推移リクエスト用の店舗コード配列を、行の queryParameter.halls から解決する。
 * 行に halls が無い場合はフォールバック店舗コードを使う。
 */
export const getTransitionHallsFromRow = ({
  rowHalls,
  fallbackHallCode,
}: GetTransitionHallsFromRowParams): string[] => {
  if (rowHalls && rowHalls.length > 0) {
    return rowHalls;
  }
  return fallbackHallCode ? [fallbackHallCode] : [];
};

/**
 * 行が台別データ推移で選択中の店舗に属するかを判定する。
 * 選択店舗が無い場合、または行に店舗情報が無い（移行期・過去データ）場合は
 * 絞り込まず候補として残す。
 */
export const isRowInSelectedHalls = (
  rowHalls: string[] | undefined,
  selectedHalls: string[] | undefined
): boolean => {
  if (!selectedHalls || selectedHalls.length === 0) {
    return true;
  }
  if (!rowHalls || rowHalls.length === 0) {
    return true;
  }
  return rowHalls.some((code) => selectedHalls.includes(code));
};

type ResolveTransitionKiListOnDaiBanChangeParams = {
  rows: DataDai['data']['rows'];
  daiBan: number;
  transitionKiList: string[];
  transitionHalls: string[] | undefined;
  daiBanColumnIndex: number;
};

/**
 * 台番号変更時の推移リクエスト用 kiList を解決する。
 * 台番セレクタは現在の機種・店舗で絞り込まれているため、推移で選択中の機種を維持する。
 */
export const resolveTransitionKiListOnDaiBanChange = ({
  rows,
  daiBan,
  transitionKiList,
  transitionHalls,
  daiBanColumnIndex,
}: ResolveTransitionKiListOnDaiBanChangeParams): string[] => {
  if (transitionKiList.length > 0) {
    return transitionKiList;
  }

  const uniqueKi = [
    ...new Set(
      rows
        .filter((row) => {
          const value = row.data.at(daiBanColumnIndex)?.value;
          if (value === undefined || parseNumber(value) !== daiBan) {
            return false;
          }
          return isRowInSelectedHalls(
            row.queryParameter.halls,
            transitionHalls
          );
        })
        .map((row) => row.queryParameter.kiList[0])
        .filter((v): v is string => v != null)
    ),
  ];

  const firstKi = uniqueKi.at(0);
  return firstKi !== undefined ? [firstKi] : [];
};

/**
 * 店舗コード配列が同一内容かを判定する（順序は問わない）。
 */
export const areSameHallCodes = (
  a: string[] | undefined,
  b: string[] | undefined
): boolean => {
  const hallsA = a ?? [];
  const hallsB = b ?? [];
  if (hallsA.length === 0 && hallsB.length === 0) {
    return true;
  }
  if (hallsA.length !== hallsB.length) {
    return false;
  }
  return hallsA.every((code) => hallsB.includes(code));
};

/**
 * 台別データ一覧の行がハイライト対象かを判定する。
 * 選択店舗が無い場合は機種のみで判定する（移行期フォールバック）。
 */
export const isUnitDataListRowHighlighted = (
  rowKiList: string[],
  rowHalls: string[] | undefined,
  selectedKiList: string[],
  selectedHalls: string[] | undefined
): boolean => {
  const selectedKi = selectedKiList.at(0);
  const rowKi = rowKiList.at(0);
  if (!selectedKi || selectedKi !== rowKi) {
    return false;
  }
  if (!selectedHalls || selectedHalls.length === 0) {
    return true;
  }
  return isRowInSelectedHalls(rowHalls, selectedHalls);
};

/**
 * クリックした行と現在のハイライト選択が同一かを判定する（トグル OFF 用）。
 */
export const isSameUnitDataListHighlightSelection = (
  kiList: string[],
  halls: string[] | undefined,
  selectedKiList: string[],
  selectedHalls: string[] | undefined
): boolean => {
  return (
    kiList.at(0) === selectedKiList.at(0) &&
    areSameHallCodes(halls, selectedHalls)
  );
};

/**
 * 店舗コード配列を settingsOptions の halls から店舗名に解決する。
 * 未解決のコードは除外し、複数店舗は読点区切りで連結する。
 */
export const resolveHallNamesFromCodes = (
  hallCodes: string[] | undefined,
  halls: Hall[]
): string => {
  if (!hallCodes || hallCodes.length === 0) {
    return '';
  }

  return hallCodes
    .map((code) => halls.find((hall) => hall.code === code)?.name ?? '')
    .filter((name) => name !== '')
    .join('、');
};
